import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Users, MapPin, UserPlus, Package } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { triggerGcalSync } from '../lib/bookings';
import { useBusiness } from '../contexts/BusinessContext';
import { Customer } from '../lib/customers';
import { getResources } from '../lib/resources';
import { registerAssistantSurface } from '../lib/assistant/surface';
import SelectCustomerModal from './manage/SelectCustomerModal';
import SelectResourceModal from './manage/SelectResourceModal';
import CreateScheduleEditor from './booking/CreateScheduleEditor';
import AddressAutocompleteModal from './AddressAutocompleteModal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialDate?: Date | null;
  /** Optional range end from calendar multi-day / multi-slot selection. */
  initialEndDate?: Date | null;
  initialResource?: { id: string; first_name: string; last_name: string; avatar_url?: string } | null;
  initialAsset?: { id: string; name: string; image_url?: string } | null;
  /** Preselected customer from the AI assistant's lookup_customer tool. */
  initialCustomer?: Customer | null;
}

export default function CreateTaskModal({
  isOpen,
  onClose,
  onSuccess,
  initialDate,
  initialEndDate = null,
  initialResource = null,
  initialAsset = null,
  initialCustomer = null,
}: CreateTaskModalProps) {
  const { selectedBusiness } = useBusiness();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
  const [assetsEnabled, setAssetsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobileTask, setIsMobileTask] = useState(false);
  const [taskAddress, setTaskAddress] = useState('');
  const [taskLat, setTaskLat] = useState<number | null>(null);
  const [taskLng, setTaskLng] = useState<number | null>(null);

  const [scheduleData, setScheduleData] = useState({
    dropOffDate: '',
    dropOffTime: '',
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '10:00'
  });

  useEffect(() => {
    if (isOpen && selectedBusiness?.id && supabase) {
      supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'teams',
        p_setting_key: 'assets_enabled',
        p_default_value: false
      }).then(({ data }) => {
        setAssetsEnabled(data === true || data === 'true');
      }).catch(() => setAssetsEnabled(false));
    }
  }, [isOpen, selectedBusiness?.id]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setTitle('');
      setNotes('');
      setSelectedCustomer(initialCustomer || null);
      setSelectedResources(initialResource ? [initialResource] : []);
      setSelectedAssets(initialAsset ? [initialAsset] : []);
      setIsMobileTask(false);
      setTaskAddress('');
      setTaskLat(null);
      setTaskLng(null);

      const dateToUse = initialDate || new Date();
      const dateStr = format(dateToUse, 'yyyy-MM-dd');
      const timeStr = initialDate ? format(initialDate, 'HH:mm') : '09:00';

      const hasRangeEnd =
        initialEndDate != null &&
        !Number.isNaN(initialEndDate.getTime()) &&
        initialEndDate.getTime() > dateToUse.getTime();
      const endToUse = hasRangeEnd
        ? initialEndDate!
        : initialDate
          ? new Date(initialDate.getTime() + 60 * 60 * 1000)
          : null;
      const endDateStr = endToUse ? format(endToUse, 'yyyy-MM-dd') : dateStr;
      const endTimeStr = endToUse ? format(endToUse, 'HH:mm') : '10:00';

      setScheduleData({
        dropOffDate: dateStr,
        dropOffTime: timeStr,
        startDate: dateStr,
        startTime: timeStr,
        endDate: endDateStr,
        endTime: endTimeStr
      });
    }
  }, [isOpen, initialDate, initialEndDate, initialResource, initialAsset, initialCustomer]);

  useEffect(() => {
    if (isMobileTask && selectedCustomer?.address) {
      setTaskAddress(selectedCustomer.address);
    }
  }, [selectedCustomer, isMobileTask]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const addOneHour = (date: string, time: string): { date: string; time: string } => {
    const [h, m] = time.split(':').map(Number);
    const newHour = h + 1;
    if (newHour >= 24) {
      const nextDay = new Date(date + 'T00:00:00');
      nextDay.setDate(nextDay.getDate() + 1);
      return {
        date: format(nextDay, 'yyyy-MM-dd'),
        time: `${String(newHour - 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      };
    }
    return { date, time: `${String(newHour).padStart(2, '0')}:${String(m).padStart(2, '0')}` };
  };

  const handleScheduleChange = (type: 'dropOff' | 'start' | 'end', date: string, time: string) => {
    setScheduleData(prev => {
      const updated = { ...prev };
      if (type === 'start') {
        updated.startDate = date;
        updated.startTime = time;
        const end = addOneHour(date, time);
        updated.endDate = end.date;
        updated.endTime = end.time;
      } else if (type === 'end') {
        const startDt = new Date(`${prev.startDate}T${prev.startTime}`);
        const proposedEndDt = new Date(`${date}T${time}`);
        if (proposedEndDt <= startDt) {
          toast.error('End date/time cannot be before or equal to start');
          return prev;
        }
        updated.endDate = date;
        updated.endTime = time;
      }
      return updated;
    });
  };

  // Mirrors the Create Event button's validation exactly, so the AI knows when
  // the form is genuinely ready (appended to every assistant handler result).
  const taskReadinessLine = () => {
    const missing: string[] = [];
    if (!title.trim()) missing.push('a title');
    const startDt = new Date(`${scheduleData.startDate}T${scheduleData.startTime || '09:00'}`);
    const endDt = new Date(`${scheduleData.endDate}T${scheduleData.endTime || '10:00'}`);
    if (!scheduleData.startDate || !scheduleData.endDate || Number.isNaN(startDt.getTime()) || endDt <= startDt) {
      missing.push('a valid schedule (end after start)');
    }
    return missing.length === 0
      ? ' FORM STATUS: READY — every required field is filled and the Create Event button is active. Once the user\'s named details are all done, tell them the event is ready and ask if you should create it. Press Create Event ONLY after they confirm.'
      : ` FORM STATUS: not ready to create yet — still needs ${missing.join(', ')}. For any missing piece the user hasn't named, ASK them — never pick a value yourself.`;
  };

  // Assistant surface handlers — reassigned every render so they always read
  // current state (same aiBridgeRef pattern as CreateBookingModal).
  const aiBridgeRef = useRef<{
    setDetails: (detail: any) => string;
    setSchedule: (detail: any) => string;
    manageResource: (detail: any) => Promise<string>;
  }>({ setDetails: () => '', setSchedule: () => '', manageResource: async () => '' });

  aiBridgeRef.current = {
    setDetails: (detail) => {
      const changed: string[] = [];
      const newTitle = String(detail?.title ?? '').trim().slice(0, 200);
      if (newTitle) {
        setTitle(newTitle);
        changed.push(`title set to "${newTitle}"`);
      }
      const newNotes = String(detail?.notes ?? '').trim().slice(0, 2000);
      if (newNotes) {
        setNotes(newNotes);
        changed.push('notes set');
      }
      const newAddress = String(detail?.address ?? '').trim().slice(0, 300);
      if (newAddress) {
        setIsMobileTask(true);
        setTaskAddress(newAddress);
        setTaskLat(null);
        setTaskLng(null);
        changed.push(`mobile event at "${newAddress}"`);
      }
      if (changed.length === 0) return 'error: nothing to set — pass a title, notes, or address.';
      // React state hasn't committed yet — compute readiness with the new title applied.
      const titleForStatus = newTitle || title;
      const startDt = new Date(`${scheduleData.startDate}T${scheduleData.startTime || '09:00'}`);
      const endDt = new Date(`${scheduleData.endDate}T${scheduleData.endTime || '10:00'}`);
      const scheduleOk = Boolean(scheduleData.startDate && scheduleData.endDate) && !Number.isNaN(startDt.getTime()) && endDt > startDt;
      const status = titleForStatus.trim() && scheduleOk
        ? ' FORM STATUS: READY — every required field is filled and the Create Event button is active. Once the user\'s named details are all done, tell them the event is ready and ask if you should create it. Press Create Event ONLY after they confirm.'
        : ` FORM STATUS: not ready to create yet — still needs ${[!titleForStatus.trim() ? 'a title' : '', !scheduleOk ? 'a valid schedule (end after start)' : ''].filter(Boolean).join(', ')}. For any missing piece the user hasn't named, ASK them — never pick a value yourself.`;
      return `Event ${changed.join(', ')}.${status}`;
    },

    setSchedule: (detail) => {
      // Partial updates: anything not passed keeps its current form value, so
      // "end at 3pm" / "make it 2 hours" work without restating the start.
      const inDate = String(detail?.date || '').trim();
      const inTime = String(detail?.time || '').trim();
      const inEndDate = String(detail?.endDate || '').trim();
      const inEndTime = String(detail?.endTime || '').trim();
      const durationHours = Number(detail?.durationHours) || 0;
      const addHours = Number(detail?.addHours) || 0;
      const startChanged = Boolean(inDate || inTime);
      if (!startChanged && !inEndDate && !inEndTime && !durationHours && !addHours) {
        return 'error: nothing to change — pass date/time, end_date/end_time, duration_hours, or add_hours.';
      }

      const date = inDate || scheduleData.startDate;
      const time = inTime || scheduleData.startTime || '09:00';
      const startDt = new Date(`${date}T${time}`);
      if (Number.isNaN(startDt.getTime())) return 'error: date must be YYYY-MM-DD and time HH:MM.';

      const pad = (n: number) => String(n).padStart(2, '0');
      let endDate: string;
      let endTime: string;
      if (addHours !== 0) {
        // Relative change ("an hour shorter") — computed from the form's
        // ACTUAL current end so the model never does the arithmetic.
        const currentEndDt = new Date(`${scheduleData.endDate || date}T${scheduleData.endTime || '10:00'}`);
        if (Number.isNaN(currentEndDt.getTime())) return 'error: the form has no valid end to adjust — pass duration_hours or end_time instead.';
        const endDt = new Date(currentEndDt.getTime() + addHours * 3600 * 1000);
        endDate = `${endDt.getFullYear()}-${pad(endDt.getMonth() + 1)}-${pad(endDt.getDate())}`;
        endTime = `${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
      } else if (durationHours > 0) {
        const endDt = new Date(startDt.getTime() + durationHours * 3600 * 1000);
        endDate = `${endDt.getFullYear()}-${pad(endDt.getMonth() + 1)}-${pad(endDt.getDate())}`;
        endTime = `${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
      } else if (inEndDate || inEndTime) {
        // "end at 3pm" keeps the current end date unless the start moved to a new day
        endDate = inEndDate || (inDate ? date : scheduleData.endDate || date);
        endTime = inEndTime || scheduleData.endTime || '10:00';
      } else {
        // Only the start moved — mirror the form: end defaults to start + 1h
        const end = addOneHour(date, time);
        endDate = end.date;
        endTime = end.time;
      }

      const endDt = new Date(`${endDate}T${endTime}`);
      if (Number.isNaN(endDt.getTime())) return 'error: end_date must be YYYY-MM-DD and end_time HH:MM.';
      if (endDt <= startDt) {
        return `error: the end (${endDate} ${endTime}) must be after the start (${date} ${time}) — pass a later end_time/end_date, or duration_hours instead.`;
      }

      setScheduleData({
        dropOffDate: date,
        dropOffTime: time,
        startDate: date,
        startTime: time,
        endDate,
        endTime,
      });
      const lengthHours = Math.round(((endDt.getTime() - startDt.getTime()) / 3600000) * 100) / 100;
      const titleOk = Boolean(title.trim());
      const status = titleOk
        ? ' FORM STATUS: READY — every required field is filled and the Create Event button is active. Once the user\'s named details are all done, tell them the event is ready and ask if you should create it. Press Create Event ONLY after they confirm.'
        : ' FORM STATUS: not ready to create yet — still needs a title. For any missing piece the user hasn\'t named, ASK them — never pick a value yourself.';
      return `Schedule set — start ${date} ${time}, end ${endDate} ${endTime} (${lengthHours}h long).${status}`;
    },

    manageResource: async (detail) => {
      const action = detail?.action === 'remove' ? 'remove' : 'add';
      const query = String(detail?.query || '').trim().toLowerCase();
      if (!query) return 'error: the resource\'s name is required.';
      const nameOf = (r: any) => (`${r.first_name || ''} ${r.last_name || ''}`.trim() || r.name || 'Unnamed');
      const listNames = (list: any[]) => (list.length ? list.map(nameOf).join(', ') : 'none');
      const tokens = query.split(/\s+/).filter(Boolean);
      const matches = (r: any) => {
        const hay = nameOf(r).toLowerCase();
        return tokens.every((t) => hay.includes(t));
      };

      if (action === 'remove') {
        const found = selectedResources.filter(matches);
        if (found.length === 0) {
          return `error: no assigned resource matches "${query}". Currently assigned: ${listNames(selectedResources)}.`;
        }
        if (found.length > 1) {
          return `error: ${found.length} assigned resources match "${query}": ${listNames(found)}. Ask the user which one.`;
        }
        setSelectedResources(prev => prev.filter(r => r.id !== found[0].id));
        return `Removed ${nameOf(found[0])} from the event. Now assigned: ${listNames(selectedResources.filter((r) => r.id !== found[0].id))}.` + taskReadinessLine();
      }

      if (!selectedBusiness?.id) return 'error: no business selected.';
      try {
        // Same roster the Assign Resources modal shows: active, bookable team
        const all = await getResources(selectedBusiness.id);
        const team = (all || []).filter((r: any) => r.status === 'active' && !r.not_bookable);
        const found = team.filter(matches);
        if (found.length === 0) {
          return `error: no team member matches "${query}". Available: ${listNames(team)}.`;
        }
        if (found.length > 1) {
          return `error: ${found.length} team members match "${query}": ${listNames(found)}. Ask the user which one.`;
        }
        if (selectedResources.some((r) => r.id === found[0].id)) {
          return `${nameOf(found[0])} is already assigned to this event. Now assigned: ${listNames(selectedResources)}.` + taskReadinessLine();
        }
        setSelectedResources(prev => [...prev, found[0]]);
        return `Added ${nameOf(found[0])} to the event. Now assigned: ${listNames([...selectedResources, found[0]])}.` + taskReadinessLine();
      } catch {
        return 'error: could not load the team list — try again.';
      }
    },
  };

  useEffect(() => {
    if (!isOpen) return;
    return registerAssistantSurface('task', {
      setDetails: (payload) => aiBridgeRef.current.setDetails(payload),
      setSchedule: (payload) => aiBridgeRef.current.setSchedule(payload),
      manageResource: (payload) => aiBridgeRef.current.manageResource(payload),
      cancel: () => {
        handleClose();
        return 'Cancelled — the event form is closed and nothing was saved.';
      },
    });
  }, [isOpen]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!title.trim()) {
      toast.error('Please enter an event title');
      return;
    }

    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    if (!scheduleData.startDate || !scheduleData.endDate) {
      toast.error('Please set a date for the event');
      return;
    }

    const startDt = new Date(`${scheduleData.startDate}T${scheduleData.startTime || '09:00'}`);
    const endDt = new Date(`${scheduleData.endDate}T${scheduleData.endTime || '10:00'}`);
    if (endDt <= startDt) {
      toast.error('End date/time cannot be before or equal to start');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('calendar_tasks')
        .insert({
          business_id: selectedBusiness.id,
          title: title.trim(),
          notes: notes.trim() || null,
          status: 'scheduled',
          customer_id: selectedCustomer?.id || null,
          start_date: scheduleData.startDate,
          start_time: scheduleData.startTime || '09:00',
          end_date: scheduleData.endDate,
          end_time: scheduleData.endTime || '10:00',
          is_mobile: isMobileTask,
          task_address: isMobileTask ? (taskAddress.trim() || null) : null,
          task_lat: isMobileTask ? taskLat : null,
          task_lng: isMobileTask ? taskLng : null,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating event:', error);
        toast.error('Failed to create event');
        return;
      }

      if (selectedResources.length > 0) {
        const resourceInserts = selectedResources.map(r => ({
          task_id: data.id,
          resource_id: r.id,
        }));
        const { error: resError } = await supabase
          .from('calendar_task_resources')
          .insert(resourceInserts);
        if (resError) {
          console.error('Error assigning resources:', resError);
        }
      }

      if (selectedAssets.length > 0) {
        const assetInserts = selectedAssets.map(a => ({
          task_id: data.id,
          asset_id: a.id,
        }));
        const { error: assetError } = await supabase
          .from('calendar_task_assets')
          .insert(assetInserts);
        if (assetError) {
          console.error('Error assigning assets:', assetError);
        }
      }

      triggerGcalSync(data.id, 'create', selectedBusiness.id, 'task');

      toast.success('Event created successfully');

      window.dispatchEvent(new CustomEvent('taskCreated', {
        detail: { taskId: data.id, businessId: selectedBusiness.id }
      }));

      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 99999,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 99999
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6">
            <h2 className="text-2xl font-bold text-gray-900">Create Event</h2>
            <p className="mt-1 text-sm text-gray-500">
              Add a new event to the calendar
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="task-title" className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="task-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter event title..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="task-notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  id="task-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule <span className="text-red-500">*</span>
                </label>
                <CreateScheduleEditor
                  dropOffDate={scheduleData.dropOffDate}
                  dropOffTime={scheduleData.dropOffTime}
                  startDate={scheduleData.startDate}
                  startTime={scheduleData.startTime}
                  endDate={scheduleData.endDate}
                  endTime={scheduleData.endTime}
                  onDateTimeChange={handleScheduleChange}
                  disabled={loading}
                  hideDropOff
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer
                </label>
                {selectedCustomer ? (
                  <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        {selectedCustomer.first_name?.[0] || ''}{selectedCustomer.last_name?.[0] || ''}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        {selectedCustomer.email && (
                          <div className="text-xs text-gray-500 truncate">{selectedCustomer.email}</div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);
                      }}
                      className="ml-3 px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSelectCustomerModalOpen(true)}
                    className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center space-x-2"
                    disabled={loading}
                  >
                    <Users className="h-4 w-4" />
                    <span>Select Customer (Optional)</span>
                  </button>
                )}
              </div>

              {/* Resources */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Resources
                </label>
                {selectedResources.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {selectedResources.map((resource) => (
                      <div key={resource.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                            {resource.avatar_url ? (
                              <img src={resource.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                            ) : (
                              <span className="text-white text-xs font-semibold">
                                {resource.first_name?.[0]}{resource.last_name?.[0]}
                              </span>
                            )}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {resource.first_name} {resource.last_name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedResources(prev => prev.filter(r => r.id !== resource.id))}
                          className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setIsSelectResourceModalOpen(true)}
                  className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center space-x-2"
                  disabled={loading}
                >
                  <UserPlus className="h-4 w-4" />
                  <span>{selectedResources.length === 0 && selectedAssets.length === 0 ? 'Assign Resources (Optional)' : 'Add Another Resource'}</span>
                </button>

                {/* Assigned Assets */}
                {assetsEnabled && selectedAssets.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {selectedAssets.map((asset) => (
                      <div key={asset.id} className="flex items-center justify-between bg-orange-50 rounded-lg p-3 border border-orange-200">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                            <Package className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">{asset.name}</span>
                            {asset.asset_type && (
                              <span className="ml-2 text-xs text-orange-600 capitalize">{asset.asset_type}</span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedAssets(prev => prev.filter(a => a.id !== asset.id))}
                          className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Event Toggle */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">Mobile Event</div>
                    <p className="text-xs text-gray-500">Event at a specific location</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newValue = !isMobileTask;
                    setIsMobileTask(newValue);
                    if (newValue && selectedCustomer?.address) {
                      setTaskAddress(selectedCustomer.address);
                    }
                    if (!newValue) {
                      setTaskAddress('');
                      setTaskLat(null);
                      setTaskLng(null);
                    }
                  }}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    isMobileTask ? 'bg-blue-500' : 'bg-gray-300'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isMobileTask ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Event Address - shown when mobile is on */}
              {isMobileTask && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Event Address
                    </label>
                    {taskAddress && selectedCustomer?.address && taskAddress !== selectedCustomer.address && (
                      <button
                        type="button"
                        onClick={() => {
                          setTaskAddress(selectedCustomer.address || '');
                          setTaskLat(null);
                          setTaskLng(null);
                        }}
                        className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        Reset to Customer Address
                      </button>
                    )}
                  </div>
                  <AddressAutocompleteModal
                    value={taskAddress}
                    onChange={(value) => setTaskAddress(value)}
                    onSelect={(address) => {
                      setTaskAddress(address.formatted);
                      setTaskLat(address.lat || null);
                      setTaskLng(address.lng || null);
                    }}
                    placeholder="Enter event location address"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  />
                </div>
              )}
            </form>
          </div>

          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSubmit()}
                disabled={loading || !title.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={(customer) => {
          setSelectedCustomer(customer);
          setIsSelectCustomerModalOpen(false);
        }}
        businessId={selectedBusiness?.id}
      />

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onConfirm={({ resources, assets }) => {
          setSelectedResources(resources);
          if (assetsEnabled) {
            setSelectedAssets(assets);
          }
        }}
        selectedResources={selectedResources}
        selectedAssetIds={selectedAssets.map(a => a.id)}
        assetsEnabled={assetsEnabled}
        confirmLabel="Confirm"
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}
